// AgentverseChat.tsx

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Button,
    Typography,
    TextField,
    Menu,
    IconButton,
    Breadcrumbs,
    Link,
    Paper,
    Avatar,
} from "@mui/material";
import {
    History as HistoryIcon,
} from "@mui/icons-material";
import MainDrawer from "../MainDrawer";
import ChatHistoryPopup, { 
    ChatHistoryItem, 
    allChats as mockChatHistory 
} from "./AgentVerseComponents/ChatHistoryPopup";
import AgentSelectionPopup, { Agent } from "./AgentVerseComponents/AgentSelectionPopup"; 
import ChatWindow, { Message } from "./AgentVerseComponents/ChatWindow";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import { RiArrowDownSLine, RiSendPlaneFill } from "react-icons/ri";

// --- THE HARDCODED QUESTIONS & DETAILED RESPONSES ---
const QUESTION_1 = "I keep getting a 500 error when trying to deploy my Kubernetes cluster. What should I do?";
const K8S_DETAILED_RESPONSE = `### 1. Check IAM and Role Binding Permissions

A \`500 Internal Server Error\` during cluster provisioning often masks a \`403 Forbidden\` at the provider level.

**Steps to resolve:**
1. Verify that the deployment service account has the necessary permissions (e.g., \`Kubernetes Engine Admin\` for GCP or \`EKSClusterPolicy\` for AWS).
2. Run a dry-run using the CLI:
\`\`\`bash
faraday cluster deploy --dry-run
\`\`\`
This will surface underlying permission faults.

### 2. Verify VPC and Subnet Configurations

Cluster deployments frequently fail with a 500 if the underlying network topology is exhausted or misconfigured.

* **Secondary IP Ranges:** Ensure your Pod and Service IP ranges do not overlap with existing subnets.
* **IP Exhaustion:** Check if your chosen subnet has enough free IPs for the requested node pool.

\`\`\`bash
# Check available IPs in your AWS subnet
aws ec2 describe-subnets --subnet-ids subnet-12345 --query "Subnets[*].AvailableIpAddressCount"
\`\`\`

### 3. Resource Quotas and Limits

You might be hitting a regional quota for vCPUs, persistent disks, or in-use IP addresses.

**Best Practices:**
* Navigate to your cloud provider's Quota Dashboard.
* Check the usage for your specific region (e.g., \`us-east-1\`).
* If you are provisioning GPU nodes, verify that you have explicit quota granted for that instance family.

### 4. Control Plane Timeouts

Sometimes, the cloud provider's API control plane experiences intermittent degradation, returning a 500 error when the API gateway times out.

**Recommended Action:**
1. Check the status page of your cloud provider (AWS Health Dashboard / GCP Status).
2. Implement an exponential backoff retry strategy in your CI/CD pipeline.
3. Try deploying into a different availability zone if the issue persists.`;

const QUESTION_2 = "Where can I find the documentation for the CLI tool?";
const CLI_DETAILED_RESPONSE = `### Official CLI Documentation

You can find the comprehensive documentation for the Faraday CLI at our official developer portal:

🔗 **[https://docs.faraday.com/cli/getting-started](https://docs.faraday.com/cli/getting-started)**

**Quick Install:**
\`\`\`bash
curl -sL https://cli.faraday.com/install.sh | bash
\`\`\`
After installation, run \`faraday --help\` to see the list of available commands.

### CLI Authentication Guide

The documentation is located at [https://docs.faraday.com/cli](https://docs.faraday.com/cli). 

For most users, the first step is setting up authentication. You can authenticate interactively or via environment variables for headless environments:

\`\`\`bash
# Interactive login
faraday login

# CI/CD headless login
export FARADAY_API_KEY="your_api_key_here"
faraday cluster list
\`\`\`

### Advanced CLI Usage & Troubleshooting

All CLI reference materials can be found at **[https://docs.faraday.com/cli/reference](https://docs.faraday.com/cli/reference)**.

If you are experiencing issues, you can append the \`--verbose\` or \`--debug\` flag to any command to get detailed stack traces and API response logs:

\`\`\`bash
faraday deploy my-service --verbose
\`\`\`
This is highly recommended before opening a support ticket.

### CI/CD Integration Documentation

If you are looking to integrate the CLI into your automated pipelines, visit **[https://docs.faraday.com/cli/ci-cd](https://docs.faraday.com/cli/ci-cd)**.

We provide native GitHub Actions, GitLab CI templates, and CircleCI orbs. Here is a basic GitHub Action snippet:

\`\`\`yaml
steps:
  - name: Install Faraday CLI
    uses: faraday/cli-setup-action@v2
    with:
      api-key: \${{ secrets.FARADAY_API_KEY }}
\`\`\``;

// --- MOCK DATA FOR AGENT SELECTION ---
const agentList: Agent[] = [
    { id: 'agent-1', name: 'AWS Agent', description: 'Specializes in AWS services like S3, EC2.', provider: 'aws' },
    { id: 'agent-2', name: 'GCP Agent', description: 'Your Google Cloud Platform expert.', provider: 'gcp' },
    { id: 'agent-3', name: 'Azure Agent', description: 'Handles Azure Active Directory queries.', provider: 'azure' },
    { id: 'agent-4', name: 'Support Agent', description: 'Your go-to expert for comprehensive technical support, debugging, and deep-dive architectural troubleshooting.', provider: 'gcp' as any },
];
// ---

/**
 * A component for the Agentverse chat interface.
 */
const AgentverseChat: React.FC = () => {
    const navigate = useNavigate();
    
    // --- State for Agent Selection ---
    const [agentNameAnchorEl, setAgentNameAnchorEl] = useState<null | HTMLElement>(null);
    // GUARANTEE Support Agent is the default
    const [selectedAgent, setSelectedAgent] = useState<Agent>(agentList.find(a => a.name === 'Support Agent') || agentList[3]); 

    // --- State for History Popup ---
    const [historyAnchorEl, setHistoryAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedChat, setSelectedChat] = useState<ChatHistoryItem>(mockChatHistory[0]);

    // --- NEW STATE FOR CHAT ---
    const [messages, setMessages] = useState<Message[]>([]);
    const [chatInput, setChatInput] = useState("");
    const [isThinking, setIsThinking] = useState(false); // Flag for the 5-7 sec animation
    // ---

    const isSendDisabled = chatInput.trim() === "" || isThinking;
    
    const handleAgentMenuClick = (event: React.MouseEvent<HTMLElement>) => {
        setAgentNameAnchorEl(event.currentTarget);
    };

    const handleAgentMenuClose = () => {
        setAgentNameAnchorEl(null);
    };

    const handleSelectAgent = (agent: Agent) => {
        setSelectedAgent(agent);
        handleAgentMenuClose();
    };

    const handleHistoryClick = (event: React.MouseEvent<HTMLElement>) => {
        setHistoryAnchorEl(event.currentTarget);
    };

    const handleHistoryClose = () => {
        setHistoryAnchorEl(null);
    };

    const handleSelectChat = (chat: ChatHistoryItem) => {
        setSelectedChat(chat);
        setChatInput(chat.prompt); 
        handleHistoryClose(); 
    };

    // --- ENHANCED HANDLER FOR SENDING CHAT (Includes Delays & Typing Effect) ---
    const handleSendQuery = (event: React.FormEvent) => {
        event.preventDefault();
        const queryText = chatInput.trim();
        if (!queryText) return;

        // 1. Add user's message
        const userMessage: Message = {
            id: `user-${Date.now()}`,
            sender: 'user',
            text: queryText,
        };
        
        // Update state and clear input immediately
        setMessages(prev => [...prev, userMessage]);
        setChatInput(""); 
        setIsThinking(true); 

        // 2. Evaluate Query against Hardcoded Questions to provide the big detailed response
        let aiResponseText = `This is a simulated AI response about "${queryText}". I can analyze architectures, write code, and assist with deep-dive technical research.`;
        
        if (queryText === QUESTION_1) {
            aiResponseText = K8S_DETAILED_RESPONSE;
        } else if (queryText === QUESTION_2) {
            aiResponseText = CLI_DETAILED_RESPONSE;
        }

        // 3. Generate Random Delay (Between 5000ms and 7000ms)
        const delay = Math.floor(Math.random() * 2000) + 5000;

        // 4. Resolve response after delay
        setTimeout(() => {
            setIsThinking(false); 

            const aiMessageId = `ai-${Date.now()}`;
            const aiResponse: Message = {
                id: aiMessageId,
                sender: 'ai',
                text: aiResponseText,
                agentName: selectedAgent.name, 
                isTyping: true, 
            };

            setMessages(prev => [...prev, aiResponse]);

            const typingDuration = (aiResponseText.length * 15) + 1000;
            setTimeout(() => {
                setMessages(currentMessages => currentMessages.map(m => 
                    m.id === aiMessageId ? { ...m, isTyping: false } : m
                ));
            }, typingDuration);

        }, delay); 
    };

    const handleStartNewChat = () => {
        setMessages([]); 
        setChatInput(""); 
        setIsThinking(false);
    };

    return (
        <Box sx={{ display: "flex", height: "100vh", overflow: "hidden" }}>
            <MainDrawer index={"aisolutions-aiverse"} />

            <Box
                component="main"
                sx={{
                    flex: 1,
                    bgcolor: "#ffffff",
                    height: "100vh", // Strict 100vh
                    display: 'flex',
                    flexDirection: 'column', 
                    overflow: 'hidden', // Stop external scrolling
                }}
            >
                {/* Fixed Top Header */}
                <Box sx={{ 
                    p: { xs: 2, sm: 2.5 }, 
                    pb: { xs: 1, sm: 1 },
                    mx: { xs: 1, sm: 2 },
                    borderBottom: '1px solid #E5E7EB',
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    flexShrink: 0 // Do not shrink
                }}>
                    <Box>
                        <Breadcrumbs 
                            aria-label="breadcrumb" 
                            sx={{ fontSize: '0.875rem', color: '#555555', fontFamily: 'Gilroy-Medium', mb:1 }}
                        >
                            <Link underline="hover" color="inherit" href="/integrations" sx={{ fontFamily: 'Gilroy-Medium' }} onClick={(e)=> {e.preventDefault(); navigate("/integrations")}}>Integrations</Link>
                            <Typography color="#1F2937" sx={{ fontFamily: 'Gilroy-Medium' }}>
                                Agentverse
                            </Typography>
                        </Breadcrumbs>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/clusters")}>
                                <ArrowBackIosNewIcon fontSize="small" />
                            </IconButton>
                            <Typography fontSize="20px" gutterBottom={false} fontFamily="Gilroy-Bold" color="#1F2937" sx={{mb: 0}}>
                                Agentverse
                            </Typography>
                        </Box>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IconButton onClick={handleHistoryClick} sx={{ border: '1px solid #D1D5DB', borderRadius: '8px', width: '40px', height: '40px' }}>
                            <HistoryIcon sx={{color: '#4B5563'}} />
                        </IconButton>
                        <Menu
                            anchorEl={historyAnchorEl}
                            open={Boolean(historyAnchorEl)}
                            onClose={handleHistoryClose}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            PaperProps={{ sx: { border: '1px solid #E0E0E0', boxShadow: '0px 4px 12px rgba(0,0,0,0.1)', mt: 1, borderRadius: '12px' } }}
                        >
                            <Box sx={{ p: 0 }}>
                                <ChatHistoryPopup 
                                    onClose={handleHistoryClose}
                                    onSelectChat={handleSelectChat}
                                    selectedChatId={selectedChat.id}
                                />
                            </Box>
                        </Menu>
                        <Button
                            variant="contained"
                            onClick={handleStartNewChat}
                            sx={{
                                backgroundColor: "#1F2937", color: "white", padding: "8px 20px",
                                fontSize: "0.875rem", textTransform: "none", fontFamily: "Gilroy-Bold",
                                borderRadius: '8px', boxShadow: 'none', '&:hover': { backgroundColor: '#374151' }
                            }}
                        >
                            Start New Chat
                        </Button>
                    </Box>
                </Box>

                {/* Fixed Agent Selection Control */}
                <Box sx={{ zIndex: 1, mb: 0, px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 2.5 }, pb: { xs: 1, sm: 1.5 }, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                    <Button
                        onClick={handleAgentMenuClick}
                        sx={{
                            border: '1px solid #E5E7EB', 
                            borderRadius: '30px', 
                            p: '4px 6px', 
                            textTransform: 'none', color: '#1F2937', fontFamily: 'Gilroy-Medium',
                            bgcolor: '#FFFFFF', boxShadow: '0px 1px 2px rgba(0,0,0,0.05)',
                        }}
                        endIcon={<RiArrowDownSLine />}
                    >
                        <Avatar
                            sx={{
                                width: 32, height: 32, mr: 1,
                                background: 'linear-gradient(90deg, rgba(255, 64, 226, 0.3) 0%, rgba(0, 100, 255, 0.1) 42%, rgba(0, 100, 255, 0.3) 100%)',
                            }}
                        >
                            <Box component="img" src="/assets/agent-image.svg" alt="Agent Icon" sx={{ width: 20, height: 20 }} />
                        </Avatar>
                        
                        <Typography sx={{fontFamily:'Gilroy-Bold', fontSize:'16px'}}>
                            {selectedAgent.name}
                        </Typography>
                    </Button>
                    <Menu
                        anchorEl={agentNameAnchorEl}
                        open={Boolean(agentNameAnchorEl)}
                        onClose={handleAgentMenuClose}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                        PaperProps={{ sx: { border: '1px solid #E0E0E0', boxShadow: '0px 4px 12px rgba(0,0,0,0.1)', mt: 1, borderRadius: '12px' } }}
                    >
                        <AgentSelectionPopup 
                            onClose={handleAgentMenuClose}
                            onSelectAgent={handleSelectAgent}
                            selectedAgentId={selectedAgent.id}
                        />
                    </Menu>
                </Box>

                {/* SCROLLABLE CHAT WINDOW AREA */}
                <Box
                    sx={{
                        flexGrow: 1, 
                        overflowY: 'auto',
                        p: { xs: 2, sm: 3 },
                        minHeight: 0 // Required for nested flex scrolling
                    }}
                >
                    <Box sx={{
                        width: '100%',
                        maxWidth: '900px', 
                        mx: 'auto',        
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%', 
                    }}>
                        <ChatWindow messages={messages} isThinking={isThinking} />
                    </Box>
                </Box>

                {/* Fixed Input Bottom Bar */}
                <Box
                    sx={{
                        p: { xs: 2, sm: 3 },
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        bgcolor: '#ffffff',
                        flexShrink: 0, // Do not shrink
                        mt: 'auto'
                    }}
                >
                    <Box sx={{ width: '100%', maxWidth: '900px', position: 'relative' }}>
                        <Paper
                            component="form"
                            onSubmit={handleSendQuery} 
                            sx={{
                                p: '8px 12px 8px 24px', 
                                display: 'flex', 
                                alignItems: 'center',
                                width: '100%', 
                                borderRadius: '40px', 
                                border: 'none',
                                boxShadow: '0px 0px 40px 4px rgba(0, 0, 0, 0.06)',
                            }}
                        >
                            <TextField
                                variant="standard"
                                placeholder="Type what you want to know here..."
                                fullWidth
                                multiline 
                                maxRows={5}
                                value={chatInput} 
                                onChange={(e) => setChatInput(e.target.value)}
                                disabled={isThinking} 
                                sx={{
                                    flex: 1, 
                                    fontFamily: 'Gilroy-Medium', 
                                    fontSize: '16px',
                                    '& .MuiInputBase-root': { padding: '0' },
                                }}
                                InputProps={{
                                    disableUnderline: true, 
                                    style: { padding: '4px 0' }
                                }}
                            />
                            <IconButton 
                                type="submit" 
                                disabled={isSendDisabled} 
                                sx={{ 
                                    p: '10px', 
                                    ml: 1, 
                                    bgcolor: isSendDisabled ? '#F3F4F6' : '#101010', 
                                    borderRadius: '50%',
                                    '&:hover': {
                                        bgcolor: isSendDisabled ? '#E5E7EB' : '#333333' 
                                    } 
                                }} 
                                aria-label="send"
                            >
                                <RiSendPlaneFill style={{ 
                                    color: isSendDisabled ? '#B0B0B0' : '#FFFFFF', 
                                    fontSize: '22px' 
                                }} />
                            </IconButton>
                        </Paper>
                        
                        <Typography
                            variant="caption"
                            sx={{
                                display: 'block', textAlign: 'center', mt: 1.5,
                                color: '#6B7280', fontFamily: 'Gilroy-Regular', fontSize: '12px'
                            }}
                        >
                            Research is happening against {' '}
                            <Box component="span" sx={{ 
                                fontFamily: 'Gilroy-SemiBold',
                                WebkitBackgroundClip: 'text',
                                backgroundClip: 'text',
                                color: '#6B7280', 
                            }}>
                                {selectedAgent.name}
                            </Box>
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default AgentverseChat;